/**
 * AppShellV2 — the v2 tenant chrome (UI-v2 Phase 1 shell fidelity).
 *
 * The `/:tenantSlug/v2` shell — reachable only via the URL path; there is no
 * product feature flag gating v2. Built to design/v2-direction/index.html.
 *
 * ── THE v2 THEME SCOPE (I11) ─────────────────────────────────────────────────
 * `data-theme="v2"` is set on a LOCAL wrapping div — NOT on <html>.
 * ThemeContext / lib/theme.ts are untouched and keep writing dark|light on <html>.
 * CSS custom properties cascade, so everything under this div re-resolves
 * against the [data-theme="v2"] scope in tokens-v2.css (light default).
 *
 * ── LIGHT/DARK TOGGLE (unified with ThemeContext) ────────────────────────────
 * The topbar moon/sun icon-btn writes the user's preference through
 * ThemeContext (`setPreference`); `data-v2-dark="true"` on this root div is
 * DERIVED from the resolved effective theme.
 *   Light → attribute absent  → [data-theme="v2"] light surfaces.
 *   Dark  → `data-v2-dark="true"` → [data-theme="v2"][data-v2-dark="true"].
 *
 * This used to be a private `useState(false)`, which meant the v2 chrome
 * ignored the stored/OS/server preference and reset to light on every load.
 * I11 still holds — the v2 token scope stays on this LOCAL div and this shell
 * never writes <html>. I11 scoped the CSS, not the state.
 *
 * ── TOPBAR REGIONS (canonical lines 687–705 + CSS 264–297) ───────────────────
 * Four regions left→right, gap:14px, padding:0 24px, height:56px:
 *   1. Breadcrumb (.breadcrumb): tenant › Dashboard (muted, 13px, gap:7px)
 *   2. Search (.cmdk): 256×34 presentational, margin-left:auto
 *   3. Theme toggle (.icon-btn): moon (light) / sun (dark), wires data-v2-dark
 *   4. Bell (.icon-btn): presentational notifications
 * NO account control in the topbar — account lives in the sidebar footer.
 *
 * Topbar bg: translucent blurred (background @ 0.82, backdrop blur/saturate).
 *
 * ── SIDEBAR ───────────────────────────────────────────────────────────────────
 * SidebarV2 is a sticky 244px rail — it manages its own height:100vh/sticky.
 * The outer shell does NOT set a max-width wrapper so the sidebar takes its
 * natural 244px and the main area gets the rest of the viewport width.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Outlet, useLocation, useParams } from "react-router-dom";
import { SidebarV2 } from "@/components/v2/layout/SidebarV2";
import { useTheme } from "@/components/theme/useTheme";
import { SearchTrigger } from "@/components/v2/search/SearchTrigger";
import { useMe } from "@/hooks/queries/auth/useMe";
import { ChevronRightSmIcon } from "@/components/v2/layout/SidebarV2";
import { Toast } from "@/components/common/Toast";
import { Modal } from "@/components/common/Modal";
import { ImpersonationBanner } from "@/components/common/ImpersonationBanner";
import { DemoBanner } from "@/components/common/DemoBanner";
import { AboveChromeSlotProvider } from "@/components/layout/AboveChromeSlot";
import { PortalHostContext } from "@/components/v2/layout/PortalHostContext";

/**
 * Map a v2 route to a human-readable breadcrumb title.
 *
 * Covers every v2 route across 1b-rem/1c/1d. The last path segment is the
 * primary key; when that segment is a literal action verb (`new`/`edit`) or a
 * dynamic id, we derive the crumb from the SECTION segment so detail/new/edit
 * routes read correctly (e.g. `jobs/:jdId/edit` → "Edit Job", `candidates/:id`
 * → "Candidate") rather than echoing a raw id. Never returns "Dashboard" as a
 * fallback — unknown segments capitalize themselves.
 */
function pageTitle(pathname: string): string {
  const segs = pathname.split("/").filter(Boolean);
  const last = segs[segs.length - 1] ?? "dashboard";

  const map: Record<string, string> = {
    dashboard: "Dashboard",
    candidates: "All Candidates",
    intelligence: "Hiring Intelligence",
    journal: "Actions Log",
    jobs: "Manage Jobs",
    jds: "Manage Jobs",
    settings: "Settings",
    help: "Help Center",
    profile: "Profile",
    security: "Security",
    team: "Team",
    billing: "Billing",
    usage: "Usage",
    activity: "Activity log",
    ai: "AI Settings",
    compliance: "Compliance",
    integrations: "Integrations",
    roles: "Roles",
    sessions: "Sessions",
    "ai-oversight": "AI Oversight",
  };

  // Section-aware crumbs for the write/detail routes (last seg is `new`/`edit`
  // or a dynamic id, so the plain map would be unhelpful).
  const section = segs.length >= 2 ? segs[segs.length - 2] : undefined;
  const sectionIsJobs = section === "jobs" || section === "jds";
  const sectionIsCandidates = section === "candidates";

  if (last === "new") {
    if (sectionIsJobs) return "New Job";
    if (sectionIsCandidates) return "Add Candidate";
    return "New";
  }
  if (last === "edit") {
    // .../jobs/:jdId/edit → section here is the id; look one more up.
    const grandparent = segs.length >= 3 ? segs[segs.length - 3] : undefined;
    if (grandparent === "jobs" || grandparent === "jds") return "Edit Job";
    return "Edit";
  }

  // Help doc pages (`/help/:slug`): humanize the slug so the crumb reads
  // "Getting Started" rather than the raw "getting-started".
  if (section === "help") {
    return last
      .split("-")
      .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1) : w))
      .join(" ");
  }

  const mapped = map[last];
  if (mapped) return mapped;

  // Dynamic-id leaf (candidate / job detail): name it after its section.
  if (sectionIsJobs) return "Job Detail";
  if (sectionIsCandidates) return "Candidate";

  return last.charAt(0).toUpperCase() + last.slice(1);
}

/**
 * Hierarchy-aware breadcrumb parent for the Settings section.
 *
 * Any `/:tenantSlug/settings/<sub>` page is a *child* of the Settings hub, so
 * the breadcrumb should read `[Tenant] › Settings › <Sub-page>` with the
 * "Settings" crumb linking back to the hub (`SettingsHubV2`,
 * `/:tenantSlug/settings`). Route-driven: any settings sub-page — present or
 * future — gets the parent crumb automatically because we key off the path
 * shape, not a hardcoded per-page list.
 *
 * Returns the hub route to link to, or `null` when the current path is not a
 * settings sub-page (the hub index itself and the standalone `/profile` route
 * return `null` — they have no Settings parent to add).
 *
 * PR4 ("the flip") — this function USED to locate the section by finding the
 * `v2` segment and reading the one after it. v2 now serves the tenant root, so
 * there is no `v2` segment and that lookup returned -1 on every real URL,
 * silently dropping the Settings parent crumb from every settings sub-page. The
 * anchor is now the mount root itself: AppShellV2 is mounted at `/:tenantSlug`,
 * so `settings` is always segment index 1. Deriving position from the mount
 * rather than from a literal path segment is the same fix `useTenantV1Base()`
 * applied to v1's pathname-coupled chrome in PR3.
 */
const SETTINGS_SEG_IDX = 1; // segs = [tenantSlug, "settings", <sub>, …]

function settingsHubHref(pathname: string): string | null {
  const segs = pathname.split("/").filter(Boolean);
  if (segs[SETTINGS_SEG_IDX] !== "settings") return null;
  // There must be at least one more segment after it — i.e. a real sub-page,
  // not the hub index (which is its own crumb and has no parent to add).
  if (SETTINGS_SEG_IDX >= segs.length - 1) return null;
  // Rebuild the hub path from the segments up to and including "settings".
  return "/" + segs.slice(0, SETTINGS_SEG_IDX + 1).join("/");
}

// ── Icon helpers (no raw color — stroke="currentColor") ─────────────────────
// `SearchIcon` moved to `components/v2/search/SearchTrigger.tsx` with the
// affordance that was its only consumer (feat-command-palette).

function MoonIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      width={17}
      height={17}
      aria-hidden
    >
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}

function SunIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      width={17}
      height={17}
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M6.3 17.7l-1.4 1.4M19.1 4.9l-1.4 1.4" />
    </svg>
  );
}

function BellIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      width={17}
      height={17}
      aria-hidden
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

// ── TopbarV2 ─────────────────────────────────────────────────────────────────

interface TopbarV2Props {
  dark: boolean;
  onToggleDark: () => void;
}

function TopbarV2({ dark, onToggleDark }: TopbarV2Props): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const me = useMe();
  const location = useLocation();

  // Breadcrumb tenant name: useMe tenant_name if available, else tenantSlug (ruling C).
  const tenantName =
    me.data?.tenant_name ?? tenantSlug ?? "";
  const currentPageTitle = pageTitle(location.pathname);
  // Hierarchy-aware crumb: on a settings sub-page, insert a clickable
  // "Settings" crumb between the tenant and the current page, linking to the
  // Settings hub. null on the hub itself / non-settings routes.
  const settingsHref = settingsHubHref(location.pathname);

  /*
   * Canonical .topbar (CSS lines 264–270):
   *   height:56px; border-bottom; flex; align-items:center; gap:14px; padding:0 24px;
   *   position:sticky; top:0; z-index:30;
   *   background: bg @ 0.82; backdrop-filter: saturate(180%) blur(10px).
   *
   * The translucent/blurred bg uses an arbitrary bg value since the token
   * has no built-in alpha variant, per spec §2 note:
   *   "use an arbitrary bg-surface-elevated/82"
   * backdrop-blur + backdrop-saturate Tailwind utilities reproduce the filter.
   */
  return (
    <header
      className={[
        "flex shrink-0 items-center border-b border-border-default",
        "sticky top-0 z-30",
        // Translucent blurred bg with 4% brand-blue veil (real-render correction — bumped from
        // 0.015 to 0.04 so the topbar veil registers alongside the visible sidebar tint).
        "bg-[linear-gradient(color-mix(in_srgb,var(--accent-primary)_4%,transparent),color-mix(in_srgb,var(--accent-primary)_4%,transparent)),color-mix(in_srgb,var(--surface-elevated)_82%,transparent)] backdrop-blur-[10px] backdrop-saturate-[1.8]",
      ].join(" ")}
      style={{ height: "56px", gap: "14px", padding: "0 24px" }}
    >
      {/*
       * 1. Breadcrumb (.breadcrumb): 13px, muted, gap:7px.
       * Canonical: <tenant> › <current screen>.
       * Hierarchy-aware for Settings: on a settings sub-page the crumb reads
       * <tenant> › Settings › <sub-page>, where "Settings" links back to the
       * Settings hub so the sub-page isn't orphaned (only the sidebar linked
       * back before). Non-settings routes keep the flat <tenant> › <screen>.
       * Wired: useMe tenant_name → tenantSlug fallback (ruling C).
       */}
      <div
        className="flex min-w-0 items-center text-text-secondary"
        style={{ fontSize: "13px", gap: "7px" }}
      >
        <span className="truncate">{tenantName}</span>
        <ChevronRightSmIcon className="shrink-0 opacity-50" />
        {settingsHref !== null ? (
          <>
            <Link
              to={settingsHref}
              className="shrink-0 cursor-pointer rounded-sm transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
            >
              Settings
            </Link>
            <ChevronRightSmIcon className="shrink-0 opacity-50" />
          </>
        ) : null}
        <b className="font-semibold text-text-primary">{currentPageTitle}</b>
      </div>

      {/*
       * 2. Search (.cmdk): margin-left:auto anchors the right cluster.
       * 256×34, border, border-radius, gap:8px, padding:0 10px.
       * FUNCTIONAL as of feat-command-palette — the markup, the ⌘K chip, and
       * every canonical measurement moved verbatim into <SearchTrigger>, which
       * also owns the ⌘K / Ctrl+K shortcut and mounts the palette dialog.
       * Rendered appearance is unchanged; only the behaviour is new.
       */}
      <SearchTrigger />

      {/*
       * 3. Theme toggle (.icon-btn#theme-toggle): 34×34, moon/sun swap.
       * THE ONE FUNCTIONAL TOPBAR CONTROL — writes the theme preference via
       * ThemeContext, which persists it (localStorage + PATCH /users/me) and
       * re-derives data-v2-dark on the shell root.
       * Moon = shown in LIGHT, Sun = shown in DARK.
       */}
      <button
        type="button"
        onClick={onToggleDark}
        title="Toggle theme"
        aria-label="Toggle v2 theme"
        className={[
          "grid shrink-0 cursor-pointer place-items-center rounded-[var(--radius,0.5rem)]",
          "border border-transparent bg-transparent text-text-secondary",
          "transition-colors hover:bg-surface-card-hover hover:text-text-primary",
        ].join(" ")}
        style={{ width: "34px", height: "34px" }}
      >
        {dark ? <SunIcon /> : <MoonIcon />}
      </button>

      {/*
       * 4. Bell (.icon-btn): 34×34, presentational — no notifications system yet.
       */}
      <button
        type="button"
        title="Notifications"
        aria-label="Notifications"
        className={[
          "grid shrink-0 cursor-pointer place-items-center rounded-[var(--radius,0.5rem)]",
          "border border-transparent bg-transparent text-text-secondary",
          "transition-colors hover:bg-surface-card-hover hover:text-text-primary",
        ].join(" ")}
        style={{ width: "34px", height: "34px" }}
      >
        <BellIcon />
      </button>
    </header>
  );
}

// ── AppShellV2 ───────────────────────────────────────────────────────────────

/**
 * Widescreen layout mode — flip this ONE constant to switch.
 *
 *   "A" (default, recommended): content is LEFT-ANCHORED to the sidebar and
 *        capped at --content-max-w, so on a widescreen it fills most of the
 *        width with only a modest right margin — sidebar → content stay
 *        connected (standard dashboard layout: GitHub / Linear / Vercel).
 *   "B": the whole app (sidebar + content) is capped at --app-max-w and
 *        centered as ONE block, giving symmetric margins OUTSIDE the app (the
 *        "app in the center" look). Content is still left-anchored to the
 *        sidebar within the block, so there is no nav↔content gap either way.
 *
 * The value is stamped onto the shell root as data-shell-layout; the centering
 * behaviour for "B" lives in tokens-v2.css. Both modes share the responsive
 * --content-max-w steps. See PR fix/v2-widescreen-content-width.
 */
const WIDESCREEN_LAYOUT: "A" | "B" = "B";

export function AppShellV2(): JSX.Element {
  /*
   * v2 dark state — drives `data-v2-dark` on the root div.
   *
   * Previously this was `useState(false)`: component-local, hardcoded to LIGHT,
   * with zero persistence. That made the v2 chrome ignore the user's actual
   * theme preference entirely — a user whose stored/OS preference is dark still
   * got a light shell on every fresh load, and any toggle evaporated on reload.
   * `<html data-theme="dark">` had NO effect here, because tailwind.config.ts
   * explicitly suppresses `dark:` inside a v2 scope that lacks `data-v2-dark`.
   *
   * Now derived from the ONE source of truth (ThemeContext → lib/theme.ts),
   * which is localStorage-backed, server-synced via PATCH /users/me, and
   * resolves `system` against `prefers-color-scheme`. Because `effective` is
   * seeded synchronously from `readStoredPreference()` in the provider's
   * useState initializer, the very FIRST render is already correct — no light
   * flash, and no dependence on /auth/me resolving.
   *
   * I11 is preserved: the ruling scopes the v2 CSS token block to a LOCAL div
   * (`data-theme="v2"`) instead of <html> so v2 overrides can't leak into v1 /
   * admin surfaces. That is untouched — this shell still writes only its own
   * div and never writes <html>. I11 constrained where the CSS scope lives, not
   * that v2 must keep a second, private copy of the theme state.
   */
  const { effective, setPreference } = useTheme();
  const dark = effective === "dark";
  const toggleDark = useCallback(() => {
    setPreference(dark ? "light" : "dark");
  }, [dark, setPreference]);

  /*
   * W0-2 (SH3): above-chrome portal target. Mirrors V1 AppShell — a stable DOM
   * node published to AboveChromeSlotProvider so pages can portal thin
   * announcement strips (sample-data, plan-limit) above the chrome. Zero height
   * when no page renders into it. `<DemoBanner/>` renders directly (not portaled)
   * and self-gates to zero height for non-demo tenants.
   */
  const aboveChromeRef = useRef<HTMLDivElement | null>(null);
  const [aboveChromeTarget, setAboveChromeTarget] =
    useState<HTMLElement | null>(null);
  useEffect(() => {
    setAboveChromeTarget(aboveChromeRef.current);
  }, []);

  /*
   * Portal host for JS-portaled overlays under the app-scale dial.
   *
   * It is a BODY-LEVEL sibling of the shell (rendered via createPortal into
   * document.body below), NOT a child of the zoomed root — deliberately. Radix
   * Popper positions its content with `position: fixed` + a screen-pixel
   * `translate`; if that fixed node sits inside a `zoom:1.25` ancestor, Chromium
   * multiplies the translate by 1.25 and the overlay lands ~25% off its trigger
   * (measured). Keeping the host at body (effective zoom 1) preserves correct
   * positioning. Scale is instead applied to the overlay CONTENT itself via
   * `[zoom:var(--app-scale)]` on each vendored primitive — the content scales in
   * place from its anchored top-left, so it matches the 125% app without moving.
   *
   * The host carries the v2 theme scope (`data-theme="v2"` + mirrored
   * `data-v2-dark`) so portaled overlays resolve v2-only tokens — notably
   * `--shadow-pop` (undefined at the base scope) and the v2 chrome tint — and so
   * `--app-scale` (defined on `[data-theme="v2"]`) resolves for the content zoom.
   * See PortalHostContext for the consumer contract.
   */
  const portalHostRef = useRef<HTMLDivElement | null>(null);
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalHost(portalHostRef.current);
  }, []);

  return (
    /*
     * v2 theme scope root. data-theme="v2" activates tokens-v2.css light block.
     * data-v2-dark="true" (when dark=true) activates the dark override block.
     * The rest of the app — outside this subtree — is completely unaffected.
     *
     * flex-col wrapper (was flex-row): the ImpersonationBanner + DemoBanner
     * stack ABOVE the sidebar+content shell-row exactly as V1's AppShell does.
     * Both banners self-gate to zero height (empty / null) for normal tenants,
     * so the default layout is unchanged and SidebarV2's sticky 100vh rail is
     * preserved.
     */
    <div
      data-theme="v2"
      {...(dark ? { "data-v2-dark": "true" } : {})}
      data-shell-layout={WIDESCREEN_LAYOUT}
      data-testid="app-shell-v2"
      className="flex min-h-screen flex-col bg-surface-canvas text-text-primary"
      /*
       * App-scale dial. `zoom` scales type + spacing + hardcoded px and reflows
       * exactly like browser zoom. THE SINGLE SOURCE OF TRUTH is the `--app-scale`
       * token in tokens-v2.css — retuning the whole app is a one-token edit there.
       * This line only wires the token to `zoom`; it holds no scale value of its
       * own (the `, 1` fallback is the neutral no-scale default when the token is
       * ever absent — e.g. outside the [data-theme="v2"] scope). Do not hardcode a
       * number here.
       */
      style={{ zoom: "var(--app-scale, 1)" }}
    >
      <PortalHostContext.Provider value={portalHost}>
      {/* W0-1 (SH2): impersonation banner — super-admin exit affordance while
          impersonating a tenant. Renders nothing for normal sessions. */}
      <ImpersonationBanner />

      <AboveChromeSlotProvider target={aboveChromeTarget}>
        {/* W0-2 (SH3): demo-tenant banner. Returns null for non-demo tenants. */}
        <DemoBanner />

        {/* Shell row — sidebar rail + content column. In layout mode "B" the
            .v2-shell-row hook lets tokens-v2.css cap this row at --app-max-w and
            center it (symmetric outer margins); in mode "A" it spans full width. */}
        <div className="v2-shell-row flex min-h-0 flex-1">
          {/* Left rail — SidebarV2 is sticky 244px (manages its own height:100vh) */}
          <SidebarV2 v2Dark={dark} />

          {/* Right column: above-chrome band + topbar + page outlet */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            {/* W0-2 (SH3): above-chrome portal target — page-owned announcement
                strips (sample-data, plan-limit) portal here, above the topbar.
                Zero height when unused. */}
            <div
              ref={aboveChromeRef}
              data-testid="above-chrome-band-v2"
              className="shrink-0"
            />

            <TopbarV2 dark={dark} onToggleDark={toggleDark} />

            {/* Page surface — full-width scroll container; content capped at --content-max-w
                and LEFT-ANCHORED to the sidebar (Option A — no mx-auto, so it sits flush
                after the rail with the slack as a right margin instead of a nav↔content
                gap). Per-page dense tables can override --content-max-w on their own root
                to widen or remove the cap. */}
            <main className="w-full flex-1 overflow-x-hidden overflow-y-auto py-content-py">
              <div className="w-full px-content-px" style={{ maxWidth: "var(--content-max-w)" }}>
                <Outlet />
              </div>
            </main>
          </div>
        </div>
      </AboveChromeSlotProvider>

      {/* W0-1 (SH1): global feedback portals. Toast + Modal render inline (no
          React portal) so mounting them inside this data-theme="v2" scope means
          any toast/modal fired anywhere in the v2 tree renders WITH v2 tokens.
          Before this, both stores had no host under the v2 shell and every v2
          toast/modal silently rendered nothing. */}
      <Toast />
      <Modal />

      {/* Body-level portal host for JS-portaled overlays (Radix Dropdown/Tooltip/
          Select Portals + the row-level delete modal). Rendered OUTSIDE the
          zoomed root (into document.body) so `position:fixed` overlays keep
          correct positioning; carries the v2 theme scope so they resolve v2
          tokens (+ `--app-scale` for their own content zoom). See the host-setup
          comment above and PortalHostContext. */}
      {createPortal(
        <div
          ref={portalHostRef}
          data-theme="v2"
          {...(dark ? { "data-v2-dark": "true" } : {})}
          data-testid="v2-portal-host"
        />,
        document.body,
      )}
      </PortalHostContext.Provider>
    </div>
  );
}
